import { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import toast from "react-hot-toast"
import { lumi } from "@/lib/lumi"
import { SITE } from "@/data/site"
import { useAuth } from "@/hooks/useAuth"

type OrderStatus = "pending" | "confirmed" | "shipped" | "cancelled"

type OrderRecord = {
  _id?: string
  name?: string
  phone?: string
  email?: string
  product?: string
  edition?: string
  quantity?: number
  price?: number
  amount?: number
  transferDate?: string
  transferAmount?: number
  accountLastFive?: string
  receiverName?: string
  receiverPhone?: string
  storeName?: string
  storeAddress?: string
  note?: string
  status?: OrderStatus
  createdAt?: string
}

const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: "待確認",
  confirmed: "已確認",
  shipped: "已出貨",
  cancelled: "已取消",
}

function AdminOrders() {
  const { isReady, isAdmin, user } = useAuth()
  const [orders, setOrders] = useState<OrderRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [keyword, setKeyword] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [selectedId, setSelectedId] = useState("")

  const loadOrders = useCallback(async () => {
    setLoading(true)
    try {
      const result = await lumi.entities.orders.list({
        sort: { createdAt: -1 },
      })
      setOrders((result?.list || []) as OrderRecord[])
    } catch (error) {
      console.error("讀取訂單失敗：", error)
      toast.error("讀取訂單失敗")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isReady) return
    if (!isAdmin) {
      window.location.href = "/admin"
      return
    }
    void loadOrders()
  }, [isReady, isAdmin, loadOrders])

  const filteredOrders = useMemo(() => {
    const query = keyword.trim().toLowerCase()
    return orders.filter((order) => {
      const matchStatus = statusFilter === "all" || order?.status === statusFilter
      if (!matchStatus) return false
      if (!query) return true
      const haystack = [
        order?.name,
        order?.phone,
        order?.email,
        order?.receiverName,
        order?.receiverPhone,
        order?.storeName,
      ]
        .join(" ")
        .toLowerCase()
      return haystack.includes(query)
    })
  }, [keyword, orders, statusFilter])

  const selectedOrder = filteredOrders.find((order) => order?._id === selectedId) || filteredOrders[0] || null

  const updateStatus = async (id: string, status: OrderStatus) => {
    try {
      await lumi.entities.orders.update(id, {
        status,
        updatedAt: new Date().toISOString(),
      })
      toast.success("狀態已更新")
      await loadOrders()
    } catch (error) {
      console.error("更新訂單失敗：", error)
      toast.error("更新訂單失敗")
    }
  }

  const deleteOrder = async (id: string) => {
    const confirmed = window.confirm("確定要刪除這筆訂單嗎？")
    if (!confirmed) return
    try {
      await lumi.entities.orders.delete(id)
      toast.success("訂單已刪除")
      setSelectedId("")
      await loadOrders()
    } catch (error) {
      console.error("刪除訂單失敗：", error)
      toast.error("刪除訂單失敗")
    }
  }

  const signOut = async () => {
    try {
      await lumi.auth.signOut()
    } catch (error) {
      console.error("登出失敗：", error)
    } finally {
      window.location.href = "/admin"
    }
  }

  if (!isReady || !isAdmin) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#080808] text-stone-400">
        載入中…
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-[#080808] text-[#f7f1e6]">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 px-6 py-5 md:px-10">
        <div>
          <p className="font-sans-ui text-[10px] tracking-[0.4em] text-[#c4a574]">ORDERS</p>
          <h1 className="font-display mt-1 text-2xl">{SITE.brandDisplay} 訂單管理</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-sans-ui text-[11px] tracking-[0.12em] text-stone-500">{user?.email || user?.userName || ""}</span>
          <Link to="/" className="font-sans-ui text-[11px] tracking-[0.16em] text-stone-400 hover:text-[#c4a574]">
            回網站
          </Link>
          <button
            type="button"
            onClick={() => void signOut()}
            className="font-sans-ui border border-white/15 px-3 py-2 text-[11px] tracking-[0.16em] text-stone-300 hover:border-[#c4a574] hover:text-[#c4a574]"
          >
            登出
          </button>
        </div>
      </header>

      <section className="grid gap-6 px-6 py-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:px-10">
        <div>
          <div className="mb-6 flex flex-col gap-3 md:flex-row">
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜尋姓名、手機、Email、門市"
              className="flex-1 border border-white/10 bg-transparent px-4 py-3 font-serif-tc text-sm text-stone-100 outline-none focus:border-[#c4a574]"
            />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="border border-white/10 bg-[#080808] px-4 py-3 font-sans-ui text-sm text-stone-200 outline-none focus:border-[#c4a574]"
            >
              <option value="all">全部狀態</option>
              <option value="pending">待確認</option>
              <option value="confirmed">已確認</option>
              <option value="shipped">已出貨</option>
              <option value="cancelled">已取消</option>
            </select>
          </div>

          {loading ? (
            <p className="font-serif-tc text-stone-500">讀取訂單中…</p>
          ) : filteredOrders.length === 0 ? (
            <p className="font-serif-tc text-stone-500">目前沒有符合條件的訂單。</p>
          ) : (
            <div className="overflow-x-auto border border-white/10">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-white/5 font-sans-ui text-[11px] tracking-[0.16em] text-stone-400">
                  <tr>
                    <th className="px-4 py-3">時間</th>
                    <th className="px-4 py-3">訂購人</th>
                    <th className="px-4 py-3">數量</th>
                    <th className="px-4 py-3">金額</th>
                    <th className="px-4 py-3">狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const id = order?._id || ""
                    const active = selectedOrder?._id === id
                    return (
                      <tr
                        key={id}
                        className={`cursor-pointer border-t border-white/10 ${active ? "bg-[#c4a574]/10" : "hover:bg-white/5"}`}
                        onClick={() => setSelectedId(id)}
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-stone-400">
                          {(order?.createdAt || "").slice(0, 16).replace("T", " ")}
                        </td>
                        <td className="px-4 py-3">
                          <p>{order?.name || "—"}</p>
                          <p className="text-xs text-stone-500">{order?.phone || ""}</p>
                        </td>
                        <td className="px-4 py-3">{order?.quantity ?? 0}</td>
                        <td className="px-4 py-3 text-[#c4a574]">NT$ {(order?.amount || 0).toLocaleString("zh-TW")}</td>
                        <td className="px-4 py-3">{STATUS_LABEL[(order?.status as OrderStatus) || "pending"]}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <aside className="border border-white/10 bg-[#0c0c0c] p-6">
          {selectedOrder ? (
            <div className="space-y-4 font-serif-tc text-sm text-stone-300">
              <h2 className="font-display text-2xl text-[#f7f1e6]">訂單詳情</h2>
              <p>商品：{selectedOrder?.product || SITE.productName} × {selectedOrder?.quantity ?? 0}</p>
              <p>版本：{selectedOrder?.edition || SITE.edition}</p>
              <p>金額：NT$ {(selectedOrder?.amount || 0).toLocaleString("zh-TW")}</p>
              <p>訂購人：{selectedOrder?.name || "—"} / {selectedOrder?.phone || "—"}</p>
              <p>Email：{selectedOrder?.email || "—"}</p>
              <p>匯款日期：{selectedOrder?.transferDate || "—"}</p>
              <p>匯款金額：{selectedOrder?.transferAmount ?? "—"}</p>
              <p>帳號末五碼：{selectedOrder?.accountLastFive || "—"}</p>
              <p>收件人：{selectedOrder?.receiverName || "—"} / {selectedOrder?.receiverPhone || "—"}</p>
              <p>門市：{selectedOrder?.storeName || "—"}</p>
              <p>地址：{selectedOrder?.storeAddress || "—"}</p>
              <p>備註：{selectedOrder?.note || "無"}</p>
              <label className="flex flex-col gap-2 pt-2">
                <span className="font-sans-ui text-[11px] tracking-[0.18em] text-stone-500">訂單狀態</span>
                <select
                  value={selectedOrder?.status || "pending"}
                  onChange={(event) => {
                    const id = selectedOrder?._id
                    if (!id) return
                    void updateStatus(id, event.target.value as OrderStatus)
                  }}
                  className="border border-white/15 bg-[#080808] px-3 py-2 outline-none focus:border-[#c4a574]"
                >
                  <option value="pending">待確認</option>
                  <option value="confirmed">已確認</option>
                  <option value="shipped">已出貨</option>
                  <option value="cancelled">已取消</option>
                </select>
              </label>
              <button
                type="button"
                onClick={() => {
                  const id = selectedOrder?._id
                  if (!id) return
                  void deleteOrder(id)
                }}
                className="font-sans-ui mt-4 border border-red-400/40 px-4 py-2 text-[11px] tracking-[0.16em] text-red-300 hover:bg-red-400/10"
              >
                刪除訂單
              </button>
            </div>
          ) : (
            <p className="font-serif-tc text-stone-500">請選擇一筆訂單。</p>
          )}
        </aside>
      </section>
    </main>
  )
}

export default AdminOrders
